import React, { useEffect, useState } from "react";
import { useQuestionsWS } from "../lib/useQuestionsWS";
import {
    fetchQuestions,
    fetchAnsweredQuestions,
    submitQuestion,
    voteQuestion,
} from "../lib/api";
import { Crown } from "./CrownIcon";
import { ArrowBigUp, MessageCircleQuestion, Send, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const NAME_KEY = "gsb_chat_name";
const VOTES_KEY = "gsb_voted_qids";

const loadVotedSet = () => {
    try {
        return new Set(JSON.parse(localStorage.getItem(VOTES_KEY) || "[]"));
    } catch {
        return new Set();
    }
};
const saveVotedSet = (set) =>
    localStorage.setItem(VOTES_KEY, JSON.stringify([...set]));

export const QASection = () => {
    const { queue, setQueue, recentAnswered, setRecentAnswered } = useQuestionsWS();
    const [name, setName] = useState(() => localStorage.getItem(NAME_KEY) || "");
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [voted, setVoted] = useState(loadVotedSet);

    // Cold-load fallback
    useEffect(() => {
        if (queue.length === 0) {
            fetchQuestions()
                .then(setQueue)
                .catch(() => {});
        }
        fetchAnsweredQuestions(20)
            .then(setRecentAnswered)
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        const n = name.trim();
        const t = text.trim();
        if (!n) return toast.error("Drop a name first 🙏");
        if (t.length < 2) return toast.error("Question is too short");
        setSending(true);
        try {
            localStorage.setItem(NAME_KEY, n);
            await submitQuestion({ username: n, text: t });
            setText("");
            toast.success("Question dropped — go upvote yours 🔥");
        } catch {
            toast.error("Couldn't submit. Try again.");
        } finally {
            setSending(false);
        }
    };

    const upvote = async (q) => {
        if (voted.has(q.id)) {
            toast.message("You already voted on this one");
            return;
        }
        // Optimistic vote
        const next = new Set(voted);
        next.add(q.id);
        setVoted(next);
        saveVotedSet(next);
        setQueue((prev) =>
            [...prev]
                .map((x) => (x.id === q.id ? { ...x, votes: x.votes + 1 } : x))
                .sort((a, b) => b.votes - a.votes),
        );
        try {
            await voteQuestion(q.id);
        } catch {
            // rollback
            const r = new Set(voted);
            r.delete(q.id);
            setVoted(r);
            saveVotedSet(r);
            toast.error("Vote failed");
        }
    };

    return (
        <section
            id="qa"
            data-testid="qa-section"
            className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24"
        >
            <div
                aria-hidden
                className="absolute inset-0 overflow-hidden pointer-events-none"
            >
                <div className="absolute top-0 left-10 w-96 h-96 rounded-full bg-skyblue/15 blur-3xl" />
                <div className="absolute bottom-0 right-10 w-96 h-96 rounded-full bg-hotpink/15 blur-3xl" />
            </div>

            <div className="relative mb-10 grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
                <div>
                    <div className="inline-flex items-center gap-2 mb-3">
                        <Crown className="w-9 h-7" color="#0AC3FF" />
                        <span className="font-anton uppercase tracking-[0.3em] text-xs text-skyblue">
                            Q&amp;A Live Queue
                        </span>
                    </div>
                    <h2
                        data-testid="qa-title"
                        className="font-anton uppercase text-5xl md:text-7xl text-white leading-[0.9] stamp-sm"
                    >
                        Ask the
                        <br />
                        <span className="text-hotpink">Preacher</span>.
                    </h2>
                    <p className="font-graffiti text-kickgreen text-2xl -rotate-2 mt-2">
                        upvotes rise to the top ↑
                    </p>
                </div>
                <p className="font-chivo text-white/60 text-sm md:text-base max-w-md">
                    Drop your question. Upvote the ones you want answered. The
                    top-voted joints get featured live on stream — your handle goes up
                    in lights when Leonard picks it.
                </p>
            </div>

            <div className="relative grid grid-cols-1 lg:grid-cols-5 gap-8">
                {/* Submit form */}
                <form
                    data-testid="qa-form"
                    onSubmit={submit}
                    className="lg:col-span-2 bg-surface border-4 border-black brutal-shadow-blue p-5 md:p-6 space-y-4 self-start"
                >
                    <div className="flex items-center gap-2">
                        <MessageCircleQuestion className="w-5 h-5 text-skyblue" />
                        <span className="font-anton uppercase tracking-widest text-xs text-white/60">
                            Drop a Question
                        </span>
                    </div>
                    <div>
                        <label className="block font-anton uppercase text-[10px] tracking-widest text-white/60 mb-1">
                            Your handle
                        </label>
                        <input
                            data-testid="qa-name-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            maxLength={40}
                            placeholder="Brother Mike from Houston"
                            className="w-full bg-black border-2 border-white/15 px-3 py-2 font-chivo text-sm text-white placeholder:text-white/30 focus:border-kickgreen focus:outline-none"
                        />
                    </div>
                    <div>
                        <label className="block font-anton uppercase text-[10px] tracking-widest text-white/60 mb-1">
                            Question
                        </label>
                        <textarea
                            data-testid="qa-text-input"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            rows={4}
                            maxLength={400}
                            placeholder="What's on your heart?"
                            className="w-full bg-black border-2 border-white/15 px-3 py-2 font-chivo text-sm text-white placeholder:text-white/30 focus:border-skyblue focus:outline-none resize-none"
                        />
                        <div className="text-right text-[10px] text-white/40 font-chivo mt-1">
                            {text.length}/400
                        </div>
                    </div>
                    <button
                        data-testid="qa-submit-btn"
                        disabled={sending}
                        className="w-full inline-flex items-center justify-center gap-2 bg-hotpink text-white font-anton uppercase tracking-wider text-base py-3 border-2 border-black hover:-translate-y-0.5 hover:shadow-[6px_6px_0_#53FC18] transition-all disabled:opacity-60"
                    >
                        <Send className="w-4 h-4" strokeWidth={3} />
                        {sending ? "Sending…" : "Submit Question"}
                    </button>
                    <p className="font-chivo text-[11px] text-white/40 text-center leading-relaxed">
                        Real-time queue · re-sorts the moment someone votes.
                    </p>
                </form>

                {/* Queue */}
                <div className="lg:col-span-3 bg-black border-4 border-black brutal-shadow-pink p-5 md:p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <Sparkles className="w-4 h-4 text-hotpink" />
                        <span className="font-anton uppercase tracking-widest text-sm text-white">
                            Top Questions Right Now
                        </span>
                        <span className="ml-auto font-chivo text-[10px] text-white/40">
                            {queue.length} in queue
                        </span>
                    </div>

                    {queue.length === 0 ? (
                        <div className="font-chivo text-white/40 text-sm text-center py-14">
                            <p className="font-graffiti text-skyblue text-3xl -rotate-2 mb-2">
                                be first ✦
                            </p>
                            No questions yet. Drop the first one.
                        </div>
                    ) : (
                        <ol className="space-y-3">
                            {queue.map((q, i) => (
                                <QuestionRow
                                    key={q.id}
                                    q={q}
                                    rank={i + 1}
                                    voted={voted.has(q.id)}
                                    onVote={() => upvote(q)}
                                />
                            ))}
                        </ol>
                    )}
                </div>
            </div>

            {/* Recently Answered */}
            {recentAnswered.length > 0 && (
                <div
                    data-testid="qa-answered-feed"
                    className="relative mt-10"
                >
                    <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
                        <div>
                            <div className="inline-flex items-center gap-2 mb-1">
                                <CheckCircle2
                                    className="w-5 h-5 text-kickgreen"
                                    strokeWidth={2.5}
                                />
                                <span className="font-anton uppercase tracking-[0.3em] text-xs text-kickgreen">
                                    Recently Answered
                                </span>
                            </div>
                            <h3 className="font-anton uppercase text-3xl md:text-4xl text-white leading-none">
                                Leonard's <span className="text-kickgreen">Answer Wall</span>
                            </h3>
                        </div>
                        <p className="font-graffiti text-hotpink text-xl -rotate-2">
                            scroll the history ↓
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {recentAnswered.map((q) => (
                            <AnsweredCard key={q.id} q={q} />
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
};

const rankColor = (rank) => {
    if (rank === 1) return "border-kickgreen text-kickgreen";
    if (rank === 2) return "border-skyblue text-skyblue";
    if (rank === 3) return "border-hotpink text-hotpink";
    return "border-white/20 text-white/40";
};

const QuestionRow = ({ q, rank, voted, onVote }) => {
    return (
        <li
            data-testid="qa-row"
            className={`flex items-stretch gap-3 bg-surface border-2 ${
                rank <= 3 ? rankColor(rank).split(" ")[0] : "border-white/10"
            } p-3 transition-colors`}
        >
            <button
                data-testid="qa-vote-btn"
                onClick={onVote}
                disabled={voted}
                className={`flex flex-col items-center justify-center gap-0.5 px-3 border-2 ${
                    voted
                        ? "border-kickgreen text-kickgreen bg-kickgreen/10"
                        : "border-white/20 text-white hover:border-kickgreen hover:text-kickgreen hover:bg-kickgreen/5"
                } transition-colors disabled:cursor-default`}
                title={voted ? "Already voted" : "Upvote"}
            >
                <ArrowBigUp
                    className={`w-5 h-5 ${voted ? "fill-kickgreen" : ""}`}
                    strokeWidth={2.5}
                />
                <span className="font-anton text-base leading-none">
                    {q.votes}
                </span>
            </button>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                    <span
                        className={`font-anton uppercase text-xs px-1.5 py-0.5 border ${rankColor(rank)}`}
                    >
                        #{rank}
                    </span>
                    <span className="font-anton uppercase tracking-wider text-sm text-white truncate">
                        {q.username}
                    </span>
                </div>
                <p className="font-chivo text-sm text-white/85 leading-snug break-words">
                    {q.text}
                </p>
            </div>
        </li>
    );
};


const AnsweredCard = ({ q }) => (
    <article
        data-testid="qa-answered-card"
        className="bg-surface border-2 border-kickgreen/40 p-4 hover:border-kickgreen hover:-translate-y-0.5 transition-all"
    >
        <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="inline-flex items-center gap-1 bg-kickgreen text-black font-anton uppercase tracking-widest text-[10px] px-2 py-0.5 border-2 border-black">
                <CheckCircle2 className="w-3 h-3" strokeWidth={3} /> Answered
            </span>
            <span className="font-anton uppercase tracking-wider text-sm text-white truncate">
                {q.username}
            </span>
            <span className="ml-auto font-chivo text-[10px] text-white/40 whitespace-nowrap">
                {q.answered_at
                    ? new Date(q.answered_at).toLocaleDateString([], {
                          month: "short",
                          day: "numeric",
                      })
                    : ""}
            </span>
        </div>
        <p className="font-chivo text-sm text-white/85 leading-snug break-words">
            {q.text}
        </p>
        {q.votes > 0 && (
            <div className="mt-2 font-anton uppercase tracking-wider text-[10px] text-kickgreen">
                ↑ {q.votes} upvote{q.votes === 1 ? "" : "s"} before answered
            </div>
        )}
    </article>
);
